// Zone selection helpers shared by the Calendar, Weekly Calendar and Map screens.
//
// A selected zone id is persisted (sessionStorage) so the screens keep the user's
// zone across reloads and navigation. That persisted id can go stale at any time:
// an admin can unassign the zone from the employee or remove it altogether, and
// the browser session still holds the old id. Sending a stale zone id to
// Calendar_api / MapGrid_api / MapCanvas_api produces a "zone not assigned"
// error instead of a usable screen.
//
// Rule enforced here: a persisted zone id is only usable when it is still present
// in the latest ZoneList_api response. Otherwise fall back to the company default
// zone (DefaultId === 1), then to the first zone in the list.

export const CALENDAR_ZONE_SESSION_KEY = "selectedZoneIDInCalendarScreen";

const asZoneArray = (zones) => (Array.isArray(zones) ? zones : []);

// ZoneList_api shape: { Data: { Records: { ZoneList: [...] } } }
export const getZoneListFromResponse = (zoneResponse) =>
  asZoneArray(zoneResponse?.Data?.Records?.ZoneList);

export const getDefaultZoneFromList = (zones = []) => {
  const list = asZoneArray(zones);
  return list.find((zone) => Number(zone?.DefaultId) === 1) || list[0] || null;
};

export const findZoneInList = (zones = [], zoneId) => {
  const targetId = Number(zoneId);
  if (!targetId) return null;

  return asZoneArray(zones).find((zone) => Number(zone?.Id) === targetId) || null;
};

// The zone a screen should actually use: the requested (persisted) zone when it is
// still in ZoneList, otherwise the default zone. Returns null for an empty list.
export const resolveZoneFromList = (zones = [], requestedZoneId = null) =>
  findZoneInList(zones, requestedZoneId) || getDefaultZoneFromList(zones);

// Single writer for the persisted calendar zone id, so a zone id that is missing
// from ZoneList can never be stored. Returns the zone that was persisted, or null
// when nothing could be resolved.
// `zones` empty means "no ZoneList to validate against yet" — the caller's id is
// kept as-is and validated on the next ZoneList_api response, rather than the
// selection being dropped.
export const persistCalendarZoneId = (zoneId, zones = []) => {
  const list = asZoneArray(zones);
  const requestedId = Number(zoneId) || 0;
  const resolvedZone = list.length > 0 ? resolveZoneFromList(list, requestedId) : null;
  const idToPersist = Number(resolvedZone?.Id ?? requestedId) || 0;

  try {
    if (!idToPersist) {
      sessionStorage.removeItem(CALENDAR_ZONE_SESSION_KEY);
      return null;
    }

    sessionStorage.setItem(CALENDAR_ZONE_SESSION_KEY, String(idToPersist));
  } catch (error) {
    console.error("Error persisting selected zone id:", error);
  }

  return resolvedZone;
};

// Session key Calendar and Map share for the last zone/member picked on either
// screen (each screen still declares its own copy of this name).
export const SHARED_SELECTIONS_KEY = "sharedMapCalendarSelections";

// Writes a zone picked on any screen to both keys Calendar, Weekly Calendar and
// Map read on load, so the next screen opens on it. With `zones`, the id is
// validated first (a zone missing from ZoneList falls back to the default zone);
// without, it is stored as-is and validated by the screen on its next load.
// Only the zone fields of the shared selections change; member/guest are kept.
export const saveZoneSelectionToSession = (zoneId, zoneName = "", zones = []) => {
  const list = asZoneArray(zones);
  const resolvedZone = list.length > 0 ? resolveZoneFromList(list, zoneId) : null;
  const id = Number(resolvedZone?.Id ?? zoneId) || 0;
  if (!id) return null;

  persistCalendarZoneId(id, list);

  try {
    const raw = sessionStorage.getItem(SHARED_SELECTIONS_KEY);
    const shared = raw ? JSON.parse(raw) : {};
    sessionStorage.setItem(
      SHARED_SELECTIONS_KEY,
      JSON.stringify({ ...shared, zoneId: id, zoneName: resolvedZone?.ZoneName || zoneName || "" })
    );
  } catch (error) {
    console.error("Error saving zone selection:", error);
  }

  return resolvedZone;
};

// Profile → Favourite zone saved: make it the zone Calendar and Map open with
// next. Both persisted ids are overwritten, otherwise the zone last picked on
// Calendar/Map would keep winning over the new favourite. There is no ZoneList
// here, so the id is validated by the screen on its next load.
export const applyFavouriteZoneToSession = (zoneId, zoneName = "") => {
  saveZoneSelectionToSession(zoneId, zoneName);
};

// The zone picked most recently on Calendar, Weekly Calendar, Map or Profile.
// The shared selections come first: Map writes only that key, while the other
// screens write both. Falls back to the Calendar key.
export const getLastSelectedZoneId = () => {
  try {
    const raw = sessionStorage.getItem(SHARED_SELECTIONS_KEY);
    const sharedZoneId = Number((raw ? JSON.parse(raw) : {})?.zoneId) || 0;
    if (sharedZoneId) return sharedZoneId;
  } catch (error) {
    console.error("Error reading shared zone selection:", error);
  }

  return getPersistedZoneId();
};

export const getPersistedZoneId = () => {
  try {
    return Number(sessionStorage.getItem(CALENDAR_ZONE_SESSION_KEY)) || null;
  } catch (error) {
    console.error("Error reading selected zone id:", error);
    return null;
  }
};
